import type { Activity, Matter, Task } from "@/shared/cache";
import {
	getFirmDefaults,
	isGouldratner,
	isNamanHowell,
} from "@/shared/utils/firm";

/**
 * Firm-aware task list. When a matter is selected, scope to that matter's
 * `task_group`; a selected matter with no task group gets no task options.
 * If the matter is not hydrated yet, keep the broad list so forms do not clear
 * valid prefilled codes before the matter cache settles. If no matter is
 * selected, fall back to the firm's default task group when one exists;
 * otherwise show the full list.
 *
 * Shared between EntryForm and the merge/group dialogs so a matter chip in
 * the dialog narrows the task picker the same way it does in add/edit.
 */
export function availableTasksForMatter(
	tasks: Task[],
	matters: Matter[],
	matterId: string | null | undefined,
): Task[] {
	const selected = matterId
		? (matters.find((m) => m.matter_id === matterId) ?? null)
		: null;
	if (matterId && !selected) return tasks;

	const selectedGroup = selected?.task_group?.trim();

	if (matterId && !selectedGroup) return [];

	if (selectedGroup) {
		return tasks.filter((t) => t.task_group === selectedGroup);
	}

	const defaultGroup = getFirmDefaults().taskGroup;
	if (defaultGroup) return tasks.filter((t) => t.task_group === defaultGroup);

	return tasks;
}

/**
 * Firm-aware activity list. When a matter is selected, scope to that matter's
 * `activity_group`; a selected matter with no activity group gets no activity
 * options. If the matter is not hydrated yet, keep the broad list so forms do
 * not clear valid prefilled codes before the matter cache settles. Gould Ratner
 * uses the firm's default activity group for options because its required
 * activity rule is driven by `task_group`. Naman Howell intentionally keeps the
 * global activity list for the same required-rule reason, but without a default
 * activity group. If no matter is selected, fall back to the firm's default
 * activity group when one exists; otherwise show the full list.
 */
export function availableActivitiesForMatter(
	activities: Activity[],
	matters: Matter[],
	matterId: string | null | undefined,
): Activity[] {
	const selected = matterId
		? (matters.find((m) => m.matter_id === matterId) ?? null)
		: null;
	if (matterId && !selected) return activities;

	const selectedGroup = selected?.activity_group?.trim();
	const defaultGroup = getFirmDefaults().activityGroup;

	if (isGouldratner() && defaultGroup) {
		const defaultActivities = activities.filter(
			(a) => a.activity_group === defaultGroup,
		);
		return defaultActivities.length > 0 ? defaultActivities : activities;
	}

	if (matterId && !selectedGroup) {
		return isNamanHowell() ? activities : [];
	}

	if (selectedGroup) {
		return activities.filter((a) => a.activity_group === selectedGroup);
	}

	if (defaultGroup) {
		return activities.filter((a) => a.activity_group === defaultGroup);
	}

	return activities;
}
